import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  archivePatient,
  assignPatientOwner,
  createWorkspacePatient,
  removePatientPhoto,
  restorePatient,
  updatePatient,
  updatePatientStatus,
  uploadPatientPhoto,
} from '@/api/patients/patientsApi';
import type {
  Patient,
  PatientOwnerChoice,
  PatientWritePayload,
} from '@/api/patients/patientsApi.types';
import {
  APPOINTMENTS_QUERY_KEY,
  CLIENTS_QUERY_KEY,
  PATIENTS_QUERY_KEY,
  buildClientPatientsQueryKey,
  buildClientQueryKey,
  buildPatientQueryKey,
} from '@/constants/queryKeys';

function usePatientInvalidation() {
  const queryClient = useQueryClient();
  return function invalidate(patient: Patient) {
    queryClient.invalidateQueries({ queryKey: PATIENTS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: buildPatientQueryKey(patient.id) });
    queryClient.invalidateQueries({ queryKey: CLIENTS_QUERY_KEY });
    if (patient.clientId !== null) {
      queryClient.invalidateQueries({ queryKey: buildClientPatientsQueryKey(patient.clientId) });
      queryClient.invalidateQueries({ queryKey: buildClientQueryKey(patient.clientId) });
    }
  };
}

interface CreatePatientVariables {
  owner: PatientOwnerChoice;
  payload: PatientWritePayload;
}

export function useCreatePatientMutation(): UseMutationResult<
  Patient,
  Error,
  CreatePatientVariables
> {
  const invalidate = usePatientInvalidation();
  return useMutation({
    mutationFn: function create(variables: CreatePatientVariables) {
      return createWorkspacePatient(variables.payload, variables.owner);
    },
    onSuccess: invalidate,
  });
}

interface AssignOwnerVariables {
  patientId: string;
  owner: Exclude<PatientOwnerChoice, { kind: 'later' }>;
}

export function useAssignPatientOwnerMutation(): UseMutationResult<
  Patient,
  Error,
  AssignOwnerVariables
> {
  const invalidate = usePatientInvalidation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function assign(variables: AssignOwnerVariables) {
      return assignPatientOwner(variables.patientId, variables.owner);
    },
    onSuccess: function invalidateOwnerViews(patient: Patient) {
      invalidate(patient);
      queryClient.invalidateQueries({ queryKey: APPOINTMENTS_QUERY_KEY });
    },
  });
}

interface UpdatePatientVariables {
  patientId: string;
  payload: PatientWritePayload;
}

export function useUpdatePatientMutation(): UseMutationResult<
  Patient,
  Error,
  UpdatePatientVariables
> {
  const invalidate = usePatientInvalidation();
  return useMutation({
    mutationFn: function update(variables: UpdatePatientVariables) {
      return updatePatient(variables.patientId, variables.payload);
    },
    onSuccess: invalidate,
  });
}

interface UpdateStatusVariables {
  patientId: string;
  status: string;
}

export function useUpdatePatientStatusMutation(): UseMutationResult<
  Patient,
  Error,
  UpdateStatusVariables
> {
  const invalidate = usePatientInvalidation();
  return useMutation({
    mutationFn: function update(variables: UpdateStatusVariables) {
      return updatePatientStatus(variables.patientId, variables.status);
    },
    onSuccess: invalidate,
  });
}

export function useArchivePatientMutation(): UseMutationResult<Patient, Error, string> {
  const invalidate = usePatientInvalidation();
  return useMutation({ mutationFn: archivePatient, onSuccess: invalidate });
}

export function useRestorePatientMutation(): UseMutationResult<Patient, Error, string> {
  const invalidate = usePatientInvalidation();
  return useMutation({ mutationFn: restorePatient, onSuccess: invalidate });
}

interface UploadPhotoVariables {
  patientId: string;
  file: File;
}

export function useUploadPatientPhotoMutation(): UseMutationResult<
  Patient,
  Error,
  UploadPhotoVariables
> {
  const invalidate = usePatientInvalidation();
  return useMutation({
    mutationFn: function upload(variables: UploadPhotoVariables) {
      return uploadPatientPhoto(variables.patientId, variables.file);
    },
    onSuccess: invalidate,
  });
}

export function useRemovePatientPhotoMutation(): UseMutationResult<Patient, Error, string> {
  const invalidate = usePatientInvalidation();
  return useMutation({ mutationFn: removePatientPhoto, onSuccess: invalidate });
}
